import _ from 'lodash';
import gettext from '@eb/gettext';
import { setWindowLocation } from '@eb/http';
import { logCheckpointOnPageChange, logCheckpointViaAjax } from '@eb/janus';
import { joinPath } from '@eb/path-utils';
import { formatUrl } from 'url-lib';
import parseUrl from 'url-lib/parseUrl';
import {
    EXPAND_LINK,
    ANCHOR_LINK,
    DIVIDER,
    ROUTER_LINK,
} from '@eb/eds-nav-list-item';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { constants } from '@eb/event-create-publish-utils';
import { getNavigationIconType } from '../components';

const {
    GA_DASHBOARD_LABEL,
    GA_MANAGE_CATEGORY,
    ACTION_LEFT_NAV_LINK_CLICKED,
    MANAGE_OLD_DASHBOARD,
    MANAGE_PRIVACY_CLICK,
    MANAGE_DISPLAY_CLICK,
    MANAGE_REFUND_POLICY_CLICK,
    MANAGE_COUNTRY_AND_CURRENCY_CLICK,
    MANAGE_TAX_CLICK,
    ACTION_NAV_LINK_CLICK,
    ACTION_NAV_MENU_TOGGLE,
    ACTION_NAV_PAYMENT_OPTIONS,
    ACTION_NAV_CHECKOUT_SETTINGS,
    ACTION_NAV_TAX_OPTIONS,
    ACTION_NAV_SHIPPING_SETTINGS,
    ACTION_NAV_DESIGN,
    ACTION_NAV_BASIC_INFO,
    ACTION_NAV_DISPLAY,
    ACTION_NAV_PRIVACY,
    CATEGORY_MANAGE,
    EVENT_DASHBOARD_BASE,
    ACTION_NAV_PUBLISH_VIEW,
} = constants;

const DISABLED_NAV_ICON_COLOR = 'ui-500';
const DEFAULT_NAV_ICON_COLOR = 'grey-800';

export const getMyEventLink = (eid) => `/myevent?eid=${eid}`;

export const isEventDashboard = (path) => path === EVENT_DASHBOARD_BASE;

// returns the PascalCase transformation of a page name used for a GA label
// foo_bar => FooBar
// bAz qUx => BazQux
export const _transformNameToGALabel = _.flow([_.camelCase, _.upperFirst]);

// returns a function that when executed (i.e. when the user clicks)
// will submit a track link google analytics event
const _trackClick = ({ action, category, label }) => {
    trackAnalyticsEvent({
        action,
        category,
        label,
        onWindowUnload: true,
    });
};

export const _getNavLinkData = (routePath) => ({
    name,
    path,
    linkContent,
    isDisabled,
    isSelected,
    showStatus,
    statusText,
    checkpoint,
    domain,
}) => ({
    name,
    path,
    isDisabled,
    showStatus,
    statusText,
    type: ANCHOR_LINK,
    content: linkContent,
    active:
        isSelected &&
        (!routePath ||
            routePath.startsWith(path) ||
            path.startsWith(routePath)),
    onClick: (e) => {
        e.preventDefault();
        _trackClick({
            action: ACTION_NAV_LINK_CLICK,
            category: CATEGORY_MANAGE,
            label: _transformNameToGALabel(name) || '',
        });

        if (checkpoint) {
            logCheckpointOnPageChange(checkpoint, domain);
        }

        setWindowLocation(path);
    },
});

const _parseManageCategoryNavItems = (categoryLinks = [], routePath) =>
    _.chain(categoryLinks)
        .map(({ name, linkData, content, category }) => {
            const items = _.chain(linkData)
                // first filter down to the linkData that is visible
                .filter(({ isVisible }) => isVisible)
                // then just map the linkData to navLink data
                .map(_getNavLinkData(routePath))
                .value();

            return {
                name,
                content,
                items,
                type: EXPAND_LINK,
                'data-automation': category,
                onActivate: () => {
                    _trackClick({
                        action: ACTION_NAV_MENU_TOGGLE,
                        category: CATEGORY_MANAGE,
                        label: _transformNameToGALabel(name) || '',
                    });
                },
            };
        })
        .filter(({ items }) => items.length)
        .value();

const _isActiveNavItem = (requestPath, navPath) => {
    const parsedRequestPath = parseUrl(requestPath);
    const parsedNavPath = parseUrl(navPath);

    return parsedRequestPath.pathname === parsedNavPath.pathname;
};

const _getNavItem = (requestPath, { path, ...otherProps }) => ({
    path,
    type: ANCHOR_LINK,
    active: _isActiveNavItem(requestPath, path),
    ...otherProps,
});

const _getPaymentOptionsLink = (
    requestPath,
    eventId,
    isPaymentOptionsShown,
    isPayoutsTabVisible,
    onClick,
) => {
    let paymentOptionsLink = null;

    if (isPaymentOptionsShown && isPayoutsTabVisible) {
        const paymentOptionText = gettext('Payout Method');

        paymentOptionsLink = _getNavItem(requestPath, {
            onClick,
            path: `/payment-options?eid=${eventId}`,
            content: paymentOptionText,
        });
    }

    return paymentOptionsLink ? [paymentOptionsLink] : [];
};

export const _typeDispatch = (isManagePage) => {
    let navigationType = ROUTER_LINK;

    if (isManagePage) {
        navigationType = ANCHOR_LINK;
    }
    return navigationType;
};

/*
 * Recursively finds first nav item whose `active` property is true
 * @param {Object[]} navItems       List of navigation items to check
 * @return {Object || undefined}    Active item or undefined if none is active
 */
export const getActiveNavItem = (navItems) => {
    const _getActiveNavItem = (navItem) => {
        if (navItem.active) {
            return navItem;
        }

        return navItem.items && getActiveNavItem(navItem.items);
    };
    // get the top level item that is either active or has a sub item that's active
    let activeItem = _.find(navItems, (item) => _getActiveNavItem(item));

    // get the actual item that's active. might be the top-level item or recursively
    // fine the sub item
    if (activeItem) {
        activeItem = _getActiveNavItem(activeItem);
    }

    return activeItem;
};

const _makeOptionalNavItem = (
    isShown,
    content,
    path,
    type,
    routePath,
    onClick,
) => {
    if (!isShown) {
        return null;
    }

    return {
        content,
        path,
        type,
        onClick,
        active: routePath === path,
    };
};

export const getTicketsLandingPage = (
    baseUrl,
    eventId,
    eventHasTiers,
    isReservedSeating,
    ticketsCompleted,
    venueId,
) => {
    if (isReservedSeating && !eventHasTiers && !ticketsCompleted) {
        return joinPath(baseUrl, eventId, 'venue', venueId);
    }
    return joinPath(baseUrl, eventId, 'tickets');
};
export const getPrivacySettingsItems = ({
    baseUrl,
    eventId,
    routePath,
    type,
    trackEvent,
}) => {
    const privacyLink = {
        content: gettext('Privacy'),
        path: joinPath(baseUrl, eventId, 'privacy'),
        type,
        onClick: () => {
            trackEvent({ action: ACTION_NAV_PRIVACY });
            logCheckpointViaAjax(MANAGE_PRIVACY_CLICK);
        },
        active: routePath === joinPath(baseUrl, eventId, 'privacy'),
    };
    const displayLink = _makeOptionalNavItem(
        true,
        gettext('Display'),
        joinPath(baseUrl, eventId, 'display'),
        type,
        routePath,
        () => {
            trackEvent({ action: ACTION_NAV_DISPLAY });
            logCheckpointViaAjax(MANAGE_DISPLAY_CLICK);
        },
    );

    return _.compact([privacyLink, displayLink]);
};

export const getPaymentsItems = ({
    showRefundSettings,
    showCheckoutSettings,
    showTaxSettings,
    showShippingSettings,
    baseUrl,
    eventId,
    type,
    routePath,
    showPaymentOptions,
    trackEvent,
    isPayoutsTabVisible,
}) => {
    const checkoutNavbarText = gettext('Payment Options');

    const refundLink = _makeOptionalNavItem(
        showRefundSettings,
        gettext('Refund Policy'),
        joinPath(baseUrl, eventId, 'refund_policy'),
        type,
        routePath,
        () => {
            _trackClick({
                action: ACTION_NAV_LINK_CLICK,
                category: CATEGORY_MANAGE,
                label: 'RefundPolicy',
            });
            logCheckpointViaAjax(MANAGE_REFUND_POLICY_CLICK);
        },
    );

    const paymentOptionsLinks = _getPaymentOptionsLink(
        routePath,
        eventId,
        showPaymentOptions,
        isPayoutsTabVisible,
        () => {
            trackEvent({ action: ACTION_NAV_PAYMENT_OPTIONS });
            _trackClick({
                action: ACTION_NAV_LINK_CLICK,
                category: CATEGORY_MANAGE,
                label: 'PayoutMethod',
            });
        },
    );

    const checkoutSettingsLink = _makeOptionalNavItem(
        showCheckoutSettings,
        checkoutNavbarText,
        joinPath(baseUrl, eventId, 'checkout'),
        type,
        routePath,
        () => {
            trackEvent({ action: ACTION_NAV_CHECKOUT_SETTINGS });
            _trackClick({
                action: ACTION_NAV_LINK_CLICK,
                category: CATEGORY_MANAGE,
                label: 'CheckoutSettings',
            });
            logCheckpointViaAjax(MANAGE_COUNTRY_AND_CURRENCY_CLICK);
        },
    );
    const taxLink = _makeOptionalNavItem(
        showTaxSettings,
        gettext('Tax'),
        joinPath(baseUrl, eventId, 'tax'),
        type,
        routePath,
        () => {
            trackEvent({ action: ACTION_NAV_TAX_OPTIONS });
            _trackClick({
                action: ACTION_NAV_LINK_CLICK,
                category: CATEGORY_MANAGE,
                label: 'Tax',
            });
            logCheckpointViaAjax(MANAGE_TAX_CLICK);
        },
    );
    const shippingLink = _makeOptionalNavItem(
        showShippingSettings,
        gettext('Shipping'),
        joinPath(baseUrl, eventId, 'shipping'),
        type,
        routePath,
        () => trackEvent({ action: ACTION_NAV_SHIPPING_SETTINGS }),
    );

    return _.compact([
        checkoutSettingsLink,
        ...paymentOptionsLinks,
        refundLink,
        shippingLink,
        taxLink,
    ]);
};

export const getSettingsItems = (options) => {
    const { baseUrl, eventId, routePath, trackEvent, type } = options;

    const [privacyLink, displayLink] = getPrivacySettingsItems({
        baseUrl,
        eventId,
        type,
        routePath,
        trackEvent,
    });
    const [
        checkoutSettingsLink,
        paymentOptionsLinks,
        taxLink,
        refundLink,
        payoutSettingsLink,
        shippingLink,
    ] = getPaymentsItems(options);

    return _.compact([
        privacyLink,
        displayLink,
        checkoutSettingsLink,
        paymentOptionsLinks,
        taxLink,
        refundLink,
        payoutSettingsLink,
        shippingLink,
    ]);
};

const getManageLink = (eventId, routePath, domain) => {
    const managePath = getMyEventLink(eventId);

    return [
        {
            type: ANCHOR_LINK,
            path: managePath,
            content: gettext('Dashboard'),
            active: managePath === formatUrl(routePath, { eid: eventId }),
            onClick: () => {
                trackAnalyticsEvent({
                    action: ACTION_LEFT_NAV_LINK_CLICKED,
                    label: GA_DASHBOARD_LABEL,
                    category: GA_MANAGE_CATEGORY,
                    dimensions: { eventId },
                });
                logCheckpointOnPageChange(MANAGE_OLD_DASHBOARD, domain);
            },
            'data-automation': 'coyote-navigation-link-dashboard',
        },
    ];
};

const getManageItems = (categoryLinks, routePath, domain) => {
    let manageItems = [];
    const manageCategories = categoryLinks
        ? _parseManageCategoryNavItems(categoryLinks, routePath, domain)
        : [];

    if (manageCategories.length) {
        manageItems = [...manageCategories, { type: DIVIDER }];
    }

    return manageItems;
};

export const getEventItems = ({
    type,
    eventId,
    baseUrl,
    routePath,
    eventHasTiers,
    isReservedSeating,
    venueId,
    isSeriesParent,
    isSeriesChild,
    seriesScheduleCompleted,
    showArtists,
    showDigitalContent,
    isDigitalContentComplete,
    artistsCompleted,
    designCompleted,
    previewCompleted,
    ticketsCompleted,
    trackEvent,
}) => {
    const basicsPath = joinPath(baseUrl, eventId, 'basicinfo');
    const schedulePath = joinPath(baseUrl, eventId, 'schedule');
    const performancesPath = joinPath(baseUrl, eventId, 'performances');
    const detailsPath = joinPath(baseUrl, eventId, 'details');
    const previewPublishPath = joinPath(baseUrl, eventId, 'preview_publish');
    const digitalContentPath = joinPath(baseUrl, eventId, 'online-event');
    const ticketsPath = getTicketsLandingPage(
        baseUrl,
        eventId,
        eventHasTiers,
        isReservedSeating,
        ticketsCompleted,
        venueId,
    );
    const isDisabled = isSeriesChild;
    const iconColor = isDisabled
        ? DISABLED_NAV_ICON_COLOR
        : DEFAULT_NAV_ICON_COLOR;
    const eventItems = [];

    // items to show when we have an eventId
    eventItems.push({
        type,
        path: basicsPath,
        content: gettext('Basic Info'),
        iconType: getNavigationIconType(0, true),
        active: routePath === basicsPath,
        onClick: () => trackEvent({ action: ACTION_NAV_BASIC_INFO }),
        iconColor,
        isDisabled,
    });

    if (isSeriesParent) {
        eventItems.push({
            type,
            path: schedulePath,
            content: gettext('Schedule'),
            iconType: getNavigationIconType(
                eventItems.length,
                seriesScheduleCompleted,
            ),
            active: routePath === schedulePath,
            iconColor,
            isDisabled,
        });
    }

    if (showArtists) {
        eventItems.push({
            type,
            path: performancesPath,
            content: gettext('Artists'),
            iconType: getNavigationIconType(
                eventItems.length,
                artistsCompleted,
            ),
            active: routePath === performancesPath,
            iconColor,
            isDisabled,
        });
    }

    eventItems.push({
        type,
        path: detailsPath,
        content: gettext('Details'),
        iconType: getNavigationIconType(eventItems.length, designCompleted),
        active: routePath === detailsPath,
        onClick: () => trackEvent({ action: ACTION_NAV_DESIGN }),
        iconColor,
        isDisabled,
    });

    if (showDigitalContent) {
        eventItems.push({
            type,
            path: digitalContentPath,
            content: gettext('Online Event Page'),
            iconType: getNavigationIconType(
                eventItems.length,
                isDigitalContentComplete,
            ),
            active: routePath === digitalContentPath,
            iconColor,
            isDisabled,
        });
    }

    eventItems.push({
        type,
        path: ticketsPath,
        content: gettext('Tickets'),
        iconType: getNavigationIconType(eventItems.length, ticketsCompleted),
        active: routePath.startsWith(ticketsPath),
        iconColor,
        isDisabled,
    });

    eventItems.push({
        type,
        path: previewPublishPath,
        content: gettext('Publish'),
        iconType: getNavigationIconType(eventItems.length, previewCompleted),
        active: routePath === previewPublishPath,
        onClick: () => trackEvent({ action: ACTION_NAV_PUBLISH_VIEW }),
        iconColor,
        isDisabled,
    });

    eventItems.push({ type: DIVIDER });

    return eventItems;
};

// KW: we should break this down into getters for each section. (ie getEventItems, getManageItems, getSettingsItems, etc.)
// Each function looking at the proper variables to determine what should be included.
// Then this function can focus on what the ordering should be
// eslint-disable-next-line complexity
export const getCoyoteNavItems = (baseUrl, eventId, domain, options = {}) => {
    const {
        routePath,
        isManagePage,
        isMusicVertical,
        isSeriesParent,
        isSeriesChild,
        categoryLinks,
        showDigitalContent,
        showRefundSettings,
        showTaxSettings,
        showCheckoutSettings,
        showPaymentOptions,
        showPerformances,
        showShippingSettings,
        trackEvent,
        eventHasTiers,
        isReservedSeating,
        venueId,
        designCompleted,
        previewCompleted,
        ticketsCompleted,
        artistsCompleted,
        seriesScheduleCompleted,
        isDigitalContentComplete,
        isPayoutsTabVisible,
        launchNewMusicCreateFlow,
    } = options;

    if (!eventId) {
        return [];
    }

    const type = _typeDispatch(isManagePage);
    const showArtists =
        isMusicVertical && showPerformances && !launchNewMusicCreateFlow;
    const settingsPaymentOptions = {
        showRefundSettings,
        showCheckoutSettings,
        showTaxSettings,
        showShippingSettings,
        baseUrl,
        eventId,
        type,
        routePath,
        showPaymentOptions,
        trackEvent,
        isPayoutsTabVisible,
    };

    const eventItems = getEventItems({
        type,
        eventId,
        baseUrl,
        routePath,
        eventHasTiers,
        isReservedSeating,
        venueId,
        isSeriesParent,
        isSeriesChild,
        seriesScheduleCompleted,
        showArtists,
        showDigitalContent,
        artistsCompleted,
        designCompleted,
        previewCompleted,
        ticketsCompleted,
        isDigitalContentComplete,
        trackEvent,
    });
    const manageLink = getManageLink(eventId, routePath, domain);
    const manageItems = getManageItems(categoryLinks, routePath, domain);

    const settingsItems = [];
    const paymentsItems = [];
    const privacySettingsItems = [];

    // Use to order the left nav
    let orderOption = [];
    let restManageItems = [];

    if (!isSeriesChild) {
        // We break all the link available under
        // settingsItems into two separate menu sections
        // so we essentially are removing the original settings items
        const paymentsLinks = getPaymentsItems(settingsPaymentOptions);

        // Don't show Payments if no
        // sub-menu items are currently available
        if (paymentsLinks.length > 0) {
            paymentsItems.push({
                type: EXPAND_LINK,
                content: gettext('Payments & Tax'),
                items: paymentsLinks,
                onActivate: () => {
                    _trackClick({
                        action: ACTION_NAV_MENU_TOGGLE,
                        category: CATEGORY_MANAGE,
                        label: _transformNameToGALabel('payments') || '',
                    });
                },
            });
        }

        privacySettingsItems.push({
            type: EXPAND_LINK,
            content: gettext('Privacy Settings'),
            items: getPrivacySettingsItems({
                baseUrl,
                eventId,
                type,
                routePath,
                trackEvent,
            }),
            onActivate: () => {
                _trackClick({
                    action: ACTION_NAV_MENU_TOGGLE,
                    category: CATEGORY_MANAGE,
                    label: _transformNameToGALabel('privacy_settings') || '',
                });
            },
        });
    }

    if (manageItems.length > 0) {
        [orderOption, ...restManageItems] = manageItems;
        orderOption = [orderOption];
    }

    return [
        ...eventItems,
        ...manageLink,
        ...orderOption,
        ...paymentsItems,
        ...restManageItems,
        ...settingsItems,
        ...privacySettingsItems,
    ];
};
